import { CartAddEvent } from '@theme/events';

/**
 * Subscription-first purchase selector (snippets/pepnow-subscription-funnel.liquid).
 * Adds the chosen variant + selling plan to the cart and opens the theme's cart drawer.
 */
class PepnowSubscriptionFunnel extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';

    if (this.dataset.mode === 'test') {
      const param = this.dataset.testParam || 'funnel';
      const testLink = new URLSearchParams(window.location.search).get(param) === 'test';
      if (!testLink && !window.Shopify?.designMode) return;
      document.documentElement.classList.add('pn-sub-on');
      this.hidden = false;
    }

    this.form = this.querySelector('[data-pn-form]');
    this.onceForm = this.querySelector('[data-pn-once-form]');
    this.cta = this.querySelector('[data-pn-cta]');
    this.note = this.querySelector('[data-pn-note]');
    this.error = this.querySelector('[data-pn-error]');
    this.sticky = this.querySelector('[data-pn-sticky]');
    this.stickyLabel = this.querySelector('[data-pn-sticky-label]');
    this.stickyCta = this.querySelector('[data-pn-sticky-cta]');
    // Fixed positioning breaks inside transformed/sticky ancestors, so the bar lives on <body>.
    if (this.sticky) document.body.append(this.sticky);

    this.form.addEventListener('change', () => this.sync());
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      const plan = this.selected;
      if (plan) this.add(event, this.form, { id: Number(plan.dataset.pnVariant), selling_plan: Number(plan.dataset.pnPlan) });
    });
    this.onceForm.addEventListener('submit', (event) => {
      this.add(event, this.onceForm, { id: Number(this.onceForm.dataset.pnVariant) });
    });
    this.stickyCta?.addEventListener('click', () => this.form.requestSubmit());

    this.sync();
    this.setupSticky();
  }

  get selected() {
    return this.form.querySelector('.pn-sub__input:checked')?.closest('.pn-sub__plan');
  }

  sync() {
    const plan = this.selected;
    if (!plan) return;

    this.cta.textContent = plan.dataset.pnCtaText;
    this.note.textContent = plan.dataset.pnNoteText;

    if (this.stickyLabel) this.stickyLabel.textContent = plan.dataset.pnStickyText;
    if (this.stickyCta) this.stickyCta.textContent = plan.dataset.pnCtaText.split(/\s[–-]\s/)[0];
  }

  /**
   * @param {SubmitEvent} event
   * @param {HTMLFormElement} form
   * @param {{ id: number, selling_plan?: number }} line - built from data attributes, never from form fields
   */
  async add(event, form, line) {
    event.preventDefault();
    const button = form.querySelector('[type="submit"]');
    if (button.disabled) return;

    const item = { ...line, quantity: 1 };

    button.disabled = true;
    this.error.hidden = true;

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [item] }),
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        this.error.textContent = result.description || result.message || 'Something went wrong. Please try again.';
        this.error.hidden = false;
        return;
      }

      const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((r) => r.json());
      document.dispatchEvent(
        new CartAddEvent({}, String(item.id), {
          source: 'pepnow-subscription-funnel',
          itemCount: cart.item_count,
          productId: this.dataset.productId,
          variantId: String(item.id),
        })
      );

      // No auto-opening cart drawer on this page: go to the cart instead of leaving the shopper guessing.
      if (!document.querySelector('cart-drawer-component[auto-open]')) {
        window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
      }
    } catch (error) {
      console.error(error);
      this.error.textContent = 'Something went wrong. Please check your connection and try again.';
      this.error.hidden = false;
    } finally {
      button.disabled = false;
    }
  }

  /** Mobile only: a bottom bar once the main button has scrolled out of view above. */
  setupSticky() {
    if (!this.sticky) return;

    let queued = false;
    const update = () => {
      queued = false;
      const scrolledPast = this.cta.getBoundingClientRect().bottom < 0;
      this.sticky.classList.toggle('is-visible', scrolledPast);
      document.documentElement.classList.toggle('pn-sub-sticky-on', scrolledPast);
    };
    const queue = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(update);
    };

    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue, { passive: true });
    update();
  }
}

if (!customElements.get('pepnow-subscription-funnel')) {
  customElements.define('pepnow-subscription-funnel', PepnowSubscriptionFunnel);
}
